// @flow
import React, {Suspense, useEffect} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import Cookie from 'js-cookie';
import Experiment from '@homeaway/react-experiment';
import GoogleOneTapLogin from '@homeaway/ha-google-one-tap';
import SiteHeader from '@homeaway/react-site-header';
import {media} from '../../utils/breakpoints';
import {TripBoardCoachMark} from '@homeaway/react-trip-board-button';
import userActions from '../../actions/user';
import {canUseDOM} from '../../utils/domUtils';
import type {LinguaProps} from '../withLingua/withLingua';
import type {InternalToolsType} from '../InternalTools/InternalTools';
import {HA_IDA_SERP_GOOGLEONETAP} from '../../abacus/experiments';
import ConnectedSkipToContentAnchor from '../ConnectedSkipToContent/ConnectedSkipToContentAnchor';

import type {Node} from 'react';
import type {State as ReduxState} from '../../reducers/index';

const TransitionBanner = React.lazy(
    /* istanbul ignore next */ () =>
        import(
            /* webpackChunkName: "lazy-transition-wrapper" */ '../LazyTransitionWrapper/LazyTransitionWrapper'
        )
);

type StateProps = {
    siteName: string,
    theme: string,
    isMobile: boolean,
    isTouchDevice: boolean,
    fullLocale: string,
    currency: string,
    brand: string,
    headerData: {},
    isUserLogged: boolean,
    marketingPreferencePageType: string
};

type DispatchProps = {
    setLoggedIn: typeof userActions.setLoggedIn
};

type OwnProps = {
    internalTools?: InternalToolsType,
    showBrandTransitionBanner: string,
    feedbackComponentLingua: {}
};

type Props = OwnProps & StateProps & LinguaProps & DispatchProps;

type ScratchpadCallback = {
    scratchpadLink: Node
};

export const HeaderBar = ({
    siteName,
    theme,
    isMobile,
    isTouchDevice,
    internalTools,
    isUserLogged,
    fullLocale,
    currency,
    showBrandTransitionBanner,
    headerData,
    feedbackComponentLingua,
    setLoggedIn,
    marketingPreferencePageType
}: Props) => {
    const [
        displayNotificationJewel,
        setDisplayNotificationJewel
    ] = React.useState(false);
    const [displayGoogleFrame, setDisplayGoogleFrame] = React.useState(false);

    useEffect(() => {
        setLoggedIn({isLoggedIn: isUserLogged});
    }, [isUserLogged, setLoggedIn]);

    const handleLoginSuccess = (options?: {publicUuid: string}) => {
        if (options && options.publicUuid) {
            window.analyticsdatalayer.publicuuid = options.publicUuid;
        }
        setLoggedIn({isLoggedIn: true});
    };
    const handleTrayChange = (opened: boolean) => {
        if (opened) {
            setDisplayGoogleFrame(true);
        } else {
            setDisplayGoogleFrame(false);
            setDisplayNotificationJewel(false);
        }
    };

    const handleGetTripBoard = ({scratchpadLink}: ScratchpadCallback) => {
        if (!isMobile) {
            return <TripBoardCoachMark scratchpadLink={scratchpadLink} />;
        }
        return null;
    };

    const siteHeaderProps = {
        dataUrl: '/serp/api/header',
        siteName,
        theme,
        isMobile: isTouchDevice,
        data: headerData,
        displayTrayJewel: displayNotificationJewel,
        fetchUserData: isUserLogged,
        fullLocale,
        onTrayChange: handleTrayChange,
        internalTools,
        currencyCode: currency,
        tripboard: handleGetTripBoard,
        withoutCountryLanguageSelector: true,
        withoutCurrencySelector: false,
        withoutLocaleSwitcher: false,
        feedbackComponentLingua,
        useUniversalLogin: true,
        marketingPreferencePageType,
        children: <ConnectedSkipToContentAnchor id="skipToSiteWideNavigation" />
    };

    const vgdcCookie = Cookie.get('vgdc');
    const vgdc = showBrandTransitionBanner || vgdcCookie;

    return (
        <div className="HeaderBar" role="group">
            <SiteHeader {...siteHeaderProps} key={isTouchDevice ? 1 : 0} />
            {canUseDOM() && showBrandTransitionBanner && (
                <Suspense fallback={null}>
                    <TransitionBanner
                        vgdc={vgdc}
                        fullLocale={fullLocale}
                        siteName={siteName}
                    />
                </Suspense>
            )}
            {!isUserLogged && (
                <Experiment name={HA_IDA_SERP_GOOGLEONETAP} variant={1}>
                    <GoogleOneTapLogin
                        displayIFrame={displayGoogleFrame}
                        onCredentialsAvailable={() =>
                            setDisplayNotificationJewel(() => true)
                        }
                        focus
                        closeOnFocusOut
                        loginSuccessCallback={handleLoginSuccess}
                    />
                </Experiment>
            )}
        </div>
    );
};

const mapStateToProps = ({
    header,
    searchParams: {params},
    system: {siteName, theme, isMobile, fullLocale, currency, brand},
    user,
    viewport: {breakpoint}
}: ReduxState): StateProps => ({
    siteName,
    theme,
    isMobile,
    isTouchDevice: isMobile || !media(breakpoint, {minWidth: 'LG_MIN'}),
    fullLocale,
    currency,
    brand,
    headerData: header,
    marketingPreferencePageType: params.pinnedPropertyListing
        ? 'pinned_serp'
        : 'serp',
    isUserLogged:
        (user && user.isLoggedIn) ||
        header.hasOwnProperty('traveler') ||
        header.hasOwnProperty('owner')
});

const mapDispatchToProps = {
    setLoggedIn: userActions.setLoggedIn
};
export default compose(connect(mapStateToProps, mapDispatchToProps))(HeaderBar);
